import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import { FormTransformers } from '@/common/utils/formTransformers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { BankInstitutionValidationSchema, createSchema } from '../../schemas/create.schema'
import { useCreateBankInstitution } from '../../hooks/useCreateBankInstitution'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import useUserPermissions from '@/common/hooks/useUserPermissions'

const Create = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<BankInstitutionValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const hasCreateBankInstitutionsPermission = !isUserPermissionsPending ? hasUserPermission('Admin::BankInstitutions::Create') : true

  const { mutate: createBankInstitution, isPending: createBankInstitutionIsPending } = useCreateBankInstitution({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bankInstitutions'] })
      goBack({ fallback: '/bank-institutions' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<BankInstitutionValidationSchema> = (data) => createBankInstitution(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={hasCreateBankInstitutionsPermission ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/bank-institutions" />

            <p className="text-white-400 font-normal">Instituições bancárias</p>

            <ChevronsRight className="text-white-400" />

            <h2>Nova instituição bancária</h2>
          </div>

          <div className="flex gap-4">
            <Link to="/bank-institutions">
              <Button variant="outline-error">Cancelar</Button>
            </Link>

            <Button
              data-testid="create_bank_institution_button"
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={createBankInstitutionIsPending}
            >
              Confirmar
            </Button>
          </div>
        </div>

        <Box className="flex max-h-[calc(100vh-128px)] flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto">
          <p className="text-lg font-bold">Novo instituição bancária</p>

          <div className="flex gap-2">
            <TextField
              label="Código"
              markAsRequired
              errorMessage={errors?.code?.message || undefined}
              className="w-1/2"
              {...register('code')}
              disabled={createBankInstitutionIsPending}
              maxLength={3}
            />
            <TextField
              label="Nome"
              markAsRequired
              errorMessage={errors?.name?.message || undefined}
              className="w-1/2"
              {...register('name')}
              disabled={createBankInstitutionIsPending}
            />
          </div>
          <div className="flex gap-2">
            <TextField
              label="Regex de validação de conta"
              errorMessage={errors?.account_validation_regex?.message || undefined}
              className="w-1/2"
              {...register('account_validation_regex')}
              disabled={createBankInstitutionIsPending}
            />
            <TextField
              label="Regex de validação de filial"
              errorMessage={errors?.branch_validation_regex?.message || undefined}
              className="w-1/2"
              {...register('branch_validation_regex')}
              disabled={createBankInstitutionIsPending}
            />
          </div>
        </Box>

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
